import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import { useIdentity } from '../../hooks/useIdentity';
import useComposerPrefill from '../../hooks/useComposerPrefill';
import CodeComposer from '../codedrop/CodeComposer';
import { startTyping, stopTyping, TYPING_TTL_MS } from './typing';
import './Composer.css';

// Throttle window for typing writes — 1 write/sec while the user is actively
// typing keeps Firestore traffic bounded.
const TYPING_THROTTLE_MS = 1000;
// Idle window before we proactively send stopTyping. Matches the TTL so the
// indicator drops at the same moment for everyone.
const TYPING_IDLE_MS = TYPING_TTL_MS;

/**
 * Composer — pill input + 44×44 round send button.
 *
 * Single-line for now (a real textarea + Shift+Enter newline support is a
 * later improvement; the spec accepts single-line as long as Enter sends).
 * The form's onSubmit handles Enter natively, so we no longer need to listen
 * for keyDown — and crucially the send <button> is type="submit", fixing the
 * legacy bug where it was type="reset" and Enter didn't send.
 *
 * Typing emission (F4.1):
 *   - On each keystroke producing non-empty text, call `startTyping` at most
 *     once per second.
 *   - Reset a 4s idle timer; if it fires, call `stopTyping`.
 *   - On submit, blur, clear, or unmount → `stopTyping`.
 *
 * Props:
 *   - onSend(text)      — called with trimmed, non-empty text only
 *   - disabled?: bool   — disables both input and send (F6.2 reconnect slot)
 *   - placeholder?      — string; defaults to "Message…"
 */
function Composer({ onSend, disabled = false, placeholder = 'Message…' }) {
  const [text, setText] = useState('');
  const [codeOpen, setCodeOpen] = useState(false);
  const { user, handle, partyCode } = useIdentity();
  const { pending: prefillHandle, consume: consumePrefill } =
    useComposerPrefill();
  const inputRef = useRef(null);

  // We want a stable reference to identity for cleanup. Reading via ref avoids
  // having to re-bind cleanup effects every time identity changes (and avoids
  // closing over a stale partyCode at unmount time).
  const identityRef = useRef({ user, handle, partyCode });
  useEffect(() => {
    identityRef.current = { user, handle, partyCode };
  }, [user, handle, partyCode]);

  // When MembersPanel's quick-reply icon fires (or any other surface calls
  // useComposerPrefill().prefill(handle)), drop `@handle ` into the input
  // and focus. The provider's `consume()` clears the slot so this effect
  // only fires once per request.
  useEffect(() => {
    if (!prefillHandle) return;
    const mention = `@${prefillHandle} `;
    setText((prev) => (prev.includes(mention) ? prev : `${mention}${prev}`));
    // Defer focus to the next frame so the new value has propagated.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        try {
          el.setSelectionRange(len, len);
        } catch {
          /* some input types don't support setSelectionRange */
        }
      }
    });
    consumePrefill();
  }, [prefillHandle, consumePrefill]);

  // Throttle bookkeeping.
  const lastTypingAtRef = useRef(0);
  const idleTimerRef = useRef(null);
  const isTypingRef = useRef(false);

  const fireStop = useCallback(() => {
    if (!isTypingRef.current) return;
    isTypingRef.current = false;
    const { user: u, partyCode: pc } = identityRef.current;
    const id = u || identityRef.current.handle;
    if (id && pc) stopTyping(pc, id);
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const scheduleIdleStop = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      fireStop();
    }, TYPING_IDLE_MS);
  }, [clearIdleTimer, fireStop]);

  const emitTyping = useCallback(() => {
    const { user: u, handle: h, partyCode: pc } = identityRef.current;
    const id = u || h;
    if (!id || !pc) return;
    const now = Date.now();
    if (now - lastTypingAtRef.current >= TYPING_THROTTLE_MS) {
      lastTypingAtRef.current = now;
      isTypingRef.current = true;
      startTyping(pc, id, h);
    } else {
      // Already broadcast within the throttle window — just mark active so
      // we know to stopTyping on submit/blur even if no new write went out.
      isTypingRef.current = true;
    }
    scheduleIdleStop();
  }, [scheduleIdleStop]);

  const handleChange = useCallback(
    (e) => {
      const next = e.target.value;
      setText(next);
      if (disabled) return;
      if (next.trim().length === 0) {
        clearIdleTimer();
        fireStop();
        return;
      }
      emitTyping();
    },
    [disabled, clearIdleTimer, fireStop, emitTyping],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (disabled) return;
      const trimmed = text.trim();
      if (!trimmed) return;
      onSend(trimmed);
      setText('');
      clearIdleTimer();
      fireStop();
    },
    [text, onSend, disabled, clearIdleTimer, fireStop],
  );

  const handleBlur = useCallback(() => {
    clearIdleTimer();
    fireStop();
  }, [clearIdleTimer, fireStop]);

  // Cleanup on unmount or party change. The party-change cleanup is critical:
  // leaving a party should drop the typing signal in the room we just left.
  useEffect(() => {
    return () => {
      clearIdleTimer();
      fireStop();
    };
  }, [partyCode, clearIdleTimer, fireStop]);

  const empty = text.trim().length === 0;

  return (
    <>
      <form className="glacier-composer" onSubmit={handleSubmit} role="search">
        <button
          type="button"
          className="glacier-composer__icon-btn"
          onClick={() => setCodeOpen(true)}
          disabled={disabled}
          aria-label="Share a code snippet"
          title="Share a code snippet"
        >
          <Icon name="code" size={20} aria-hidden />
        </button>
        <label className="glacier-composer__field">
          <span className="sr-only">Message</span>
          <input
            ref={inputRef}
            className="glacier-composer__input"
            type="text"
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            aria-label="Message"
            autoComplete="off"
            enterKeyHint="send"
          />
        </label>
        <button
          type="submit"
          className="glacier-composer__send"
          disabled={disabled || empty}
          aria-label="Send message"
        >
          <Icon name="send" size={20} aria-hidden />
        </button>
      </form>
      <CodeComposer isOpen={codeOpen} onClose={() => setCodeOpen(false)} />
    </>
  );
}

export default Composer;
